import React, { useEffect, useState } from 'react'
import type { InboxConversationSummary } from 'components/inbox/inboxTypes'
import type { Review as ReviewType } from 'components/supplier_show/prop_shapes/review'
import { MessageModalLayout } from 'components/inbox/shared/MessageModalLayout'
import { formatReviewerRecipientName, handleAvatarError } from 'components/inbox/utils'

interface Props {
  review: ReviewType
  conversations: InboxConversationSummary[]
  onClose: () => void
  onStartNewConversation: () => void
  onOpenExistingConversation: (conversationId: number) => void
}

const reviewAuthorParticipant = (review: ReviewType): { fullName: string, avatarUrl: string } => ({
  fullName: formatReviewerRecipientName(review.author.name, review.is_anonymous === true),
  avatarUrl: review.author.thumbnail
})

const conversationLabel = (
  conversation: InboxConversationSummary
): string => {
  if (conversation.participants.length === 0) return 'Existing private message'
  return conversation.participants.map((participant) => participant.fullName).join(', ')
}

export const PrivateMessageChooserModal: React.FC<Props> = ({
  review,
  conversations,
  onClose,
  onStartNewConversation,
  onOpenExistingConversation
}) => {
  const [selectedOption, setSelectedOption] = useState<'new' | `existing-${number}`>(
    conversations[0] != null ? `existing-${conversations[0].id}` : 'new'
  )
  const newOptionId = `new-private-conversation-option-${review.id}`
  const reviewAuthor = reviewAuthorParticipant(review)
  const newConversationParticipants = [reviewAuthor]

  const handleSelectOption = (option: 'new' | `existing-${number}`): void => {
    setSelectedOption(option)
  }

  useEffect(() => {
    const defaultOption = conversations[0] != null ? `existing-${conversations[0].id}` as const : 'new'
    const selectedConversationStillExists = selectedOption.startsWith('existing-') &&
      conversations.some((conversation) => conversation.id === Number(selectedOption.replace('existing-', '')))

    if (selectedOption === 'new' || selectedConversationStillExists) return

    setSelectedOption(defaultOption)
  }, [conversations, selectedOption])

  const handleNext = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault()
    event.stopPropagation()

    if (selectedOption.startsWith('existing-')) {
      const selectedConversationId = Number(selectedOption.replace('existing-', ''))
      onOpenExistingConversation(selectedConversationId)
      return
    }

    if (selectedOption === 'new') {
      onStartNewConversation()
    }
  }

  return (
    <MessageModalLayout
      review={review}
      title='Private Message'
      onClose={onClose}
      testId='private-message-chooser-modal'
      showReviewTile={false}
    >
      <div className='private-message-modal__chooser'>
        <div className='private-message-modal__intro'>
          <p className='private-message-modal__notice'>
            <strong>A conversation about this review already exists.</strong>
          </p>

          <p className='private-message-modal__description'>
            Select the conversation below to send a message to existing participants or start a new conversation with a different set of participants.
          </p>
        </div>

        <div className='private-message-modal__chooser-list'>
          {conversations.map((conversation) => {
            const existingOptionId = `existing-private-conversation-option-${conversation.id}`
            const existingOptionValue = `existing-${conversation.id}` as const

            return (
              <div key={conversation.id} className='private-message-modal__chooser-row'>
                <input
                  id={existingOptionId}
                  className='private-message-modal__chooser-input'
                  type='radio'
                  name={`private-message-choice-${review.id}`}
                  checked={selectedOption === existingOptionValue}
                  onChange={() => handleSelectOption(existingOptionValue)}
                />
                <label
                  htmlFor={existingOptionId}
                  className='private-message-modal__chooser-checkbox-label'
                >
                  <span
                    className={`private-message-modal__chooser-checkbox${selectedOption === existingOptionValue ? ' private-message-modal__chooser-checkbox--selected' : ''}`}
                    aria-hidden='true'
                  >
                    {selectedOption === existingOptionValue && (
                      <span className='private-message-modal__chooser-checkbox-dot' />
                    )}
                  </span>
                </label>
                <label
                  htmlFor={existingOptionId}
                  className='private-message-modal__chooser-option'
                >
                  <span className='private-message-modal__chooser-option-content'>
                    <span className='private-message-modal__chooser-summary'>
                      <span className='private-message-modal__chooser-avatars message-reviewer-modal__recipient-list' aria-hidden='true'>
                        <img
                          className='message-reviewer-modal__recipient-avatar'
                          src={reviewAuthor.avatarUrl}
                          alt=''
                          onError={(event) => handleAvatarError(event.currentTarget)}
                        />
                      </span>
                      <span className='private-message-modal__chooser-details'>
                        <span className='private-message-modal__chooser-title'>{conversationLabel(conversation)}</span>
                        <span className='private-message-modal__chooser-preview'>
                          {conversation.lastMessageText === '' ? 'Open existing conversation' : conversation.lastMessageText}
                        </span>
                      </span>
                    </span>
                  </span>
                </label>
              </div>
            )
          })}

          <div className='private-message-modal__chooser-row'>
            <input
              id={newOptionId}
              className='private-message-modal__chooser-input'
              type='radio'
              name={`private-message-choice-${review.id}`}
              checked={selectedOption === 'new'}
              onChange={() => handleSelectOption('new')}
            />
            <label
              htmlFor={newOptionId}
              className='private-message-modal__chooser-checkbox-label'
            >
              <span
                className={`private-message-modal__chooser-checkbox${selectedOption === 'new' ? ' private-message-modal__chooser-checkbox--selected' : ''}`}
                aria-hidden='true'
              >
                {selectedOption === 'new' && (
                  <span className='private-message-modal__chooser-checkbox-dot' />
                )}
              </span>
            </label>
            <label
              htmlFor={newOptionId}
              className='private-message-modal__chooser-option'
            >
              <span className='private-message-modal__chooser-option-content'>
                <span className='private-message-modal__chooser-summary'>
                  <span className='private-message-modal__chooser-avatars message-reviewer-modal__recipient-list' aria-hidden='true'>
                    {newConversationParticipants.map((participant) => (
                      <img
                        key={`${participant.fullName}-${participant.avatarUrl ?? 'no-avatar'}`}
                        className='message-reviewer-modal__recipient-avatar'
                        src={participant.avatarUrl}
                        alt=''
                        onError={(event) => handleAvatarError(event.currentTarget)}
                      />
                    ))}
                  </span>
                  <span className='private-message-modal__chooser-title'>Start new conversation</span>
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className='private-message-modal__chooser-actions'>
          <button
            type='button'
            className='write-message-cancel'
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onClose()
            }}
          >
            Cancel
          </button>

          <button
            type='button'
            className='write-message-send'
            onClick={handleNext}
          >
            Next
          </button>
        </div>
      </div>
    </MessageModalLayout>
  )
}
